import { useState } from "react";

export type PlaybookToSend = {
  sequenceId: number;
  name: string;
  touches: number;
  email: number;
  sms: number;
};

type Props = {
  playbooks: PlaybookToSend[];
  isSending: boolean;
  onCancel: () => void;
  onSend: () => void;
};

/*
    The confirmation before a playbook reaches a client (MPN-2207).

    It exists to say the one thing the Ops user cannot see from the tab: how many messages
    each playbook carries, and that each goes as its OWN request — so a client with two
    playbooks gets two to-dos, not one. Sending is not reversible from here; the client is
    emailed the moment it goes.
*/
export function SendToApprovalModal({ playbooks, isSending, onCancel, onSend }: Props) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const total = playbooks.reduce((sum, p) => sum + p.touches, 0);

  return (
    <div
      className="esms-modal__scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Send to Approval"
      onClick={onCancel}
    >
      <div className="esms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="esms-modal__head">
          <h5>Send to Approval</h5>
          <button type="button" className="esms-modal__x" aria-label="Close" onClick={onCancel}>
            ×
          </button>
        </div>

        <div className="esms-modal__body">
          {playbooks.map((p) => (
            <div className="esms-modal__pb" key={p.sequenceId}>
              <strong>{p.name}</strong>
              <span className="esms-modal__sep">|</span>
              <span>
                {p.touches} {p.touches === 1 ? "touch" : "touches"}
                {p.email > 0 && `, ${p.email} email`}
                {p.sms > 0 && `, ${p.sms} SMS`}
              </span>
            </div>
          ))}

          <p className="esms-modal__note">
            Each playbook goes as its own request. The client will get an email and a to-do in
            the platform.
          </p>

          <button
            type="button"
            className="esms-modal__preview"
            onClick={() => setPreviewOpen((open) => !open)}
            aria-expanded={previewOpen}
          >
            ✉ Preview notification email
          </button>

          {previewOpen && (
            /*
                The real email is composed server side and rate limited to one an hour per
                engagement, so this shows its shape rather than pretending to render it.
            */
            <div className="esms-modal__email">
              <div className="esms-modal__email-sub">
                Content is ready for your review
              </div>
              <p>
                {playbooks.length === 1
                  ? `${playbooks[0].name} is ready for you to review`
                  : `${playbooks.length} sequences are ready for you to review`}{" "}
                — {total} {total === 1 ? "message" : "messages"} in total. Review them in the
                platform, where each one is shown as the student will receive it.
              </p>
              <p className="esms-modal__email-foot">
                Sent to the engagement's client reviewers. At most one of these an hour.
              </p>
            </div>
          )}
        </div>

        <div className="esms-modal__foot">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={onCancel}
            disabled={isSending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={onSend}
            disabled={isSending || playbooks.length === 0}
          >
            {isSending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SendToApprovalModal;
